// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// NAVEGAR NO MAPA COM AS SETAS — a decisão, sem DOM e sem React
// ============================================================================
//
// 📗 §12 do desenho (`docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`), a
//    peça **L** — «o teclado», medido a **zero**: *«não são atalhos — é a selecção como objecto»*.
//    A peça **G** deixou declarado: *«NAVEGAR (selecção + setas + trazer à vista) continua 🅿️ —
//    medido: não há `ArrowUp`/`ArrowDown` no `Mapa.tsx`»*. Confirmado a 2026-09-15: o único
//    `keydown` do ficheiro era o `Ctrl+K` da paleta de comandos.
//
// ⚠️⚠️ **PORQUE É QUE ISTO VIVE AQUI E NÃO NO `Mapa.tsx`:** são três decisões que se provam sem
//    desenhar nada — que tecla move o quê, que linha vem a seguir quando há linhas escondidas, e
//    quanto é preciso deslocar para a célula caber. 📌 Dentro do componente só se provavam
//    através do ecrã, e é nas três que os erros de fronteira vivem (o `trechos-de-condutor.ts`
//    existe pela mesma razão: *«é onde estes algoritmos falham — falta sempre um dos quatro
//    casos, e o que falta é o que ninguém testa»*).
//
// ⚠️ **O ORÇAMENTO DE DESENHO NÃO SE TOCA (ADR-001 / risco R1).** Nada aqui cria nós: quem chama
//    põe **um** ouvinte no `window` — como o `Ctrl+K` — e o que muda é a selecção que já existia.
//    Um ouvinte por célula seriam ~600 por janela, e o p95 nota-o.
// ============================================================================

/** O passo de uma tecla: linhas (`dv`) e dias (`dd`). `null` = não é uma tecla de navegação. */
export interface PassoDaTecla {
  readonly dv: number;
  readonly dd: number;
}

/**
 * ⚠️ **As quatro setas e mais nada.** `Home`/`End`/`PageUp`/`PageDown`/`Enter`/`Escape` não são
 * deltas — cada uma pede uma acção que depende dos DADOS da célula seleccionada (o extento da
 * corrida, uma página de linhas, o painel), e por isso vivem em `teclaDeSalto` e são decididas
 * em `Mapa.tsx`, que é onde essa informação já existe. ⚠️ Este comentário dizia «não foram
 * pedidas» — passaram a sê-lo (§12.4, medido a 2026-09-28): a frase morreu e ficou, o mesmo
 * defeito que este projecto já pagou noutros ficheiros.
 */
export function passoDaTecla(tecla: string): PassoDaTecla | null {
  switch (tecla) {
    case 'ArrowUp':
      return { dv: -1, dd: 0 };
    case 'ArrowDown':
      return { dv: 1, dd: 0 };
    case 'ArrowLeft':
      return { dv: 0, dd: -1 };
    case 'ArrowRight':
      return { dv: 0, dd: 1 };
    default:
      return null;
  }
}

/** As seis teclas de SALTO do §12.4 — as que não são um delta de posição. */
export type TeclaDeSalto = 'Home' | 'End' | 'PageUp' | 'PageDown' | 'Enter' | 'Escape';

const TECLAS_DE_SALTO: ReadonlySet<string> = new Set<TeclaDeSalto>([
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Enter',
  'Escape',
]);

/**
 * Reconhece as teclas de SALTO do §12.4: `Home`/`End` (início/fim do serviço),
 * `PageUp`/`PageDown` (uma página do ecrã) e `Enter`/`Esc` (abrir/fechar o Detalhe).
 *
 * ⚠️ Ao contrário de `passoDaTecla`, não devolve um delta de posição: o que cada uma faz depende
 * do serviço sob a selecção (`extentoDaCorrida`/`extentoDoServico`, já em `Mapa.tsx`) ou do
 * painel — dados que não vivem neste ficheiro sem DOM. Esta função só separa «é uma destas seis»
 * de «não é», para o `keydown` decidir o resto.
 */
export function teclaDeSalto(tecla: string): TeclaDeSalto | null {
  return TECLAS_DE_SALTO.has(tecla) ? (tecla as TeclaDeSalto) : null;
}

/**
 * Quantas linhas cabem numa página do ecrã — o passo do `PageUp`/`PageDown` (§12.4, "no ecrã").
 *
 * ⚠️ **Mínimo 1.** Numa janela minúscula — ou em teste, onde `clientHeight` é `0` em jsdom —
 * uma página de zero linhas fazia `PageDown` indistinguível de não premir tecla nenhuma, que é
 * pior do que a seta simples (essa pelo menos anda uma linha).
 *
 * 📌 **Usa a altura POR OMISSÃO da linha, não a real de cada uma** (D-085 deixa esticar linhas à
 * mão). Somar o vector de excepções para saber exactamente quantas cabem tornaria uma página —
 * que é uma aproximação por natureza, a mesma que uma folha de cálculo faz quando as linhas têm
 * alturas diferentes — numa segunda geometria a manter sincronizada com a primeira.
 */
export function linhasPorPagina(alturaVisivel: number, alturaLinha: number): number {
  return Math.max(1, Math.floor(alturaVisivel / alturaLinha));
}

/**
 * 2026-10-27 · O foco está num sítio onde se ESCREVE (campo, selector ou zona editável)?
 *
 * 📌 Extraído de `oFocoEstaLivre` para o `Ctrl+Z` do plano (`Desfazer.tsx`) usar a MESMA leitura:
 * o ouvinte global fazia `preventDefault()` sem olhar para o alvo, e quem carregava em Ctrl+Z num
 * motivo perdia o desfazer do texto e via abrir-se o cartão do plano. ⚠️ Não é `oFocoEstaLivre`
 * inteiro: dentro de uma janela, num botão, o Ctrl+Z do plano continua a valer — só um campo de
 * escrita é dono do atalho. Duas leituras de «é um campo» divergiriam.
 */
export function oFocoEEscrita(alvo: unknown): boolean {
  if (alvo === null || alvo === undefined) return false;
  const el = alvo as { tagName?: unknown; isContentEditable?: unknown; closest?: (s: string) => unknown };
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'OPTION') return true;
  if (el.isContentEditable === true) return true;
  // ⚠️ `closest` porque o `isContentEditable` não existe no jsdom das provas; `false` desliga.
  return (
    typeof el.closest === 'function' &&
    el.closest('[contenteditable]:not([contenteditable="false"])') !== null
  );
}

/**
 * ⚠️⚠️ **A SETA NÃO PODE ROUBAR A ESCRITA A NINGUÉM.**
 *
 * 📌 O comentário do `Ctrl+K` já regista a lição na forma dele: *«uma tecla sem modificador
 * roubava a escrita a todos os campos de texto do ecrã — as notas da célula, o motivo da
 * divergência, os km reais»*. As setas são piores do que uma letra: dentro de um `<input>`
 * movem o cursor, dentro de um `<select>` **mudam o valor gravado**, e num `<textarea>` andam
 * pelas linhas. Tomá-las sem olhar para onde está o foco fazia o mapa saltar enquanto alguém
 * escrevia um motivo — e mudava a escolha de um selector sem ninguém ver.
 *
 * ⚠️ E uma janela aberta (`.janela`, `[role="dialog"]`, `[role="menu"]`) tem a atenção toda:
 * mexer na selecção por trás dela é mudar o alvo dos botões que ela mostra.
 */
export function oFocoEstaLivre(alvo: unknown): boolean {
  if (alvo === null || alvo === undefined) return true;
  if (oFocoEEscrita(alvo)) return false;
  const el = alvo as { closest?: (s: string) => unknown };
  if (typeof el.closest === 'function') {
    // ⚠️ `closest` e não uma comparação com o próprio elemento: o foco pode estar num botão
    //    DENTRO da janela, e o botão não é a janela.
    if (el.closest('.janela, [role="dialog"], [role="menu"]') !== null) return false;
  }
  return true;
}

/**
 * A linha seguinte na direcção `passo`, **saltando as escondidas** (B₂).
 *
 * ⚠️⚠️ **Uma linha escondida tem altura ZERO e não desenha nó nenhum**, logo parar nela punha a
 * selecção — e o painel de detalhe, e o alvo de todos os botões — numa viatura que **não está no
 * ecrã**. 📌 O planeador carregava na seta, nada se mexia à vista, e o gesto seguinte agia noutra
 * viatura: é «um valor plausível no lugar do certo» com consequência de escrita.
 *
 * ⚠️ **Não dá a volta.** Passar do dia 365 para o dia 1 da viatura seguinte é um salto que
 * ninguém pediu, e num mapa de 250 × 365 quem o apanha não percebe para onde foi. No limite,
 * fica-se onde se está — e o `v` devolvido é o mesmo, o que quem chama lê como «não há para onde».
 */
export function proximaLinha(
  v: number,
  passo: number,
  nLinhas: number,
  escondidas?: ReadonlySet<number> | null,
): number {
  if (passo === 0) return v;
  for (let i = v + passo; i >= 0 && i < nLinhas; i += passo) {
    if (escondidas === null || escondidas === undefined || !escondidas.has(i)) return i;
  }
  return v;
}

/** O dia seguinte, preso ao ano. Não dá a volta, pela mesma razão de `proximaLinha`. */
export function proximoDia(d: number, passo: number, nDias: number): number {
  return Math.min(nDias - 1, Math.max(0, d + passo));
}

/**
 * A linha de uma PÁGINA (`PageUp`/`PageDown`, §12.4) — anda `passo` posições, mas PRENDE-SE aos
 * limites da frota em vez de «não mexer nada».
 *
 * ⚠️⚠️ **`proximaLinha` não serve aqui sem adaptação, e foi medido a falhar antes de se
 * escrever esta função.** Ela avança por SALTOS DO MESMO TAMANHO a partir de fora dos limites
 * (`i += passo`, começando em `v + passo`) — e quando o primeiro salto já sai da grelha (o caso
 * normal perto do fim, ou em qualquer grelha mais pequena do que uma página, como um *fixture*
 * de teste), o ciclo nunca chega a entrar e devolve `v` inalterado. Uma tecla que às vezes «não
 * faz nada» perto do bordo é pior do que uma sem esse caso: ensina a largá-la e usar o rato.
 *
 * 📌 **A saída: prender o alvo aos limites PRIMEIRO, e só depois procurar uma linha visível** —
 * a partir do alvo já preso, andando para DENTRO da grelha (na direcção oposta ao passo), porque
 * é aí que está a próxima linha visível quando o próprio limite calha numa linha escondida.
 */
export function linhaDaPagina(
  v: number,
  passo: number,
  nLinhas: number,
  escondidas?: ReadonlySet<number> | null,
): number {
  if (passo === 0 || nLinhas <= 0) return v;
  const alvo = Math.max(0, Math.min(nLinhas - 1, v + passo));
  if (escondidas === null || escondidas === undefined || !escondidas.has(alvo)) return alvo;
  const paraDentro = passo > 0 ? -1 : 1;
  for (let i = alvo + paraDentro; i >= 0 && i < nLinhas; i += paraDentro) {
    if (!escondidas.has(i)) return i;
  }
  return v;
}

/**
 * Quanto é preciso deslocar para que `[i, f]` caiba na janela `[vis0, vis1]` — **0 quando já
 * cabe**, e é esse zero que faz a diferença entre navegar e sacudir o ecrã.
 *
 * ⚠️⚠️ **NÃO é o `irPara`, e a distinção é a funcionalidade.** O `irPara` responde a «leva-me a
 * um sítio que não estou a ver» e por isso **recentra** com um terço de folga e acende o realce
 * de 9 s. Uma seta responde a «anda uma célula»: recentrar a cada tecla fazia o mapa saltar
 * debaixo do olho a quem está a percorrer uma linha, e acender um realce a cada tecla
 * transformava o realce em decoração — que é exactamente a razão por que ele se apaga sozinho.
 *
 * ⚠️ A `margem` é uma célula de contexto **na direcção do movimento**: encostar a célula ao
 * bordo esconde o que vem a seguir, que é o que se está a ir ver.
 */
export function deslocarParaCaber(
  i: number,
  f: number,
  vis0: number,
  vis1: number,
  margem: number,
): number {
  if (i < vis0) return i - vis0 - margem;
  if (f > vis1) return f - vis1 + margem;
  return 0;
}
